import { useEffect, useMemo } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import { object, string, array, boolean, number, SchemaOf } from 'yup';
import { ArrowLeftRight, CheckCircle, AlertCircle } from 'lucide-react';

import { Widget, WidgetBody } from '@@/Widget';
import { FormSectionTitle } from '@@/form-components/FormSectionTitle';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { Switch } from '@@/form-components/SwitchField/Switch';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { LoadingButton } from '@@/buttons';
import { TextTip } from '@@/Tip/TextTip';

import { TypedPolicy } from '../../../types';
import { useNameValidation } from '../../../validation/useNameValidation';
import { PolicyConfigurationFormSection } from '../../PolicyConfigurationFormSection';
import { PolicyFormFooter } from '../../PolicyFormFooter';
import { useTestObservabilityConnectionMutation } from '../../../queries/useTestObservabilityConnectionMutation';

import {
  ObservabilityK8sFormValues,
  ObservabilityLogsMode,
  ObservabilityPreset,
  OBSERVABILITY_PRESETS,
  LOGS_MODE_OPTIONS,
  isLoopbackURL,
} from './types';
import { useKubernetesObservabilityInitialValues } from './useKubernetesObservabilityInitialValues';

function ObservabilityTestConnection({
  url,
  apiKey,
  projectID,
  tlsSkipVerify,
  policyId,
  autoRotateKey,
  autoProvisionIngestionKey,
  onTestSuccess,
}: {
  url: string;
  apiKey: string;
  projectID: string;
  tlsSkipVerify: boolean;
  policyId?: number;
  autoRotateKey: boolean;
  autoProvisionIngestionKey: boolean;
  onTestSuccess?: () => void;
}) {
  const testMutation = useTestObservabilityConnectionMutation();

  const disabled = !url || (!apiKey && !policyId) || !projectID;
  const testResult = testMutation.data ?? null;

  return (
    <div className="flex flex-row items-center gap-3">
      <LoadingButton
        size="small"
        color="default"
        className="!ml-0 w-min"
        isLoading={testMutation.isLoading}
        icon={ArrowLeftRight}
        loadingText="Testing connection..."
        onClick={handleTest}
        disabled={disabled}
        type="button"
        data-cy="observability-test-connection-button"
      >
        Test connection and permission
      </LoadingButton>

      {testResult && (
        <TextTip
          className="!items-start [&>svg]:mt-0.5"
          icon={testResult.success ? CheckCircle : AlertCircle}
          color={testResult.success ? 'green' : 'red'}
        >
          {testResult.message ??
            (testResult.success
              ? 'Connection successful.'
              : 'Connection failed.')}
        </TextTip>
      )}
    </div>
  );

  function handleTest() {
    testMutation.mutate(
      {
        oneUptimeURL: url,
        apiKey,
        projectID,
        tlsSkipVerify,
        policyId,
        autoRotateKey,
        autoProvisionIngestionKey,
      },
      {
        onSuccess: (data) => {
          if (data.success) onTestSuccess?.();
        },
      }
    );
  }
}

// Resets connectionVerified whenever an input that changes what the test validates changes.
// This prevents submitting with auto-rotation enabled after swapping credentials without re-testing.
function ConnectionVerifiedReset() {
  const { values, setFieldValue } =
    useFormikContext<ObservabilityK8sFormValues>();

  useEffect(() => {
    setFieldValue('connectionVerified', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.oneUptimeURL,
    values.apiKey,
    values.projectID,
    values.autoRotateKey,
  ]);

  return null;
}

interface Props {
  onSubmit: (values: ObservabilityK8sFormValues) => Promise<void>;
  mode: 'create' | 'edit';
  policy?: TypedPolicy;
  templateData?: Record<string, unknown>;
  onBack?: () => void;
  canGoBack?: boolean;
}

export function ObservabilityK8sPolicyForm({
  onSubmit,
  mode,
  policy,
  templateData,
  onBack,
  canGoBack,
}: Props) {
  const initialValues = useKubernetesObservabilityInitialValues(
    policy,
    templateData
  );
  const originalEnvironmentGroupIds = policy?.EnvironmentGroups || [];
  const nameValidation = useNameValidation(policy?.Id);
  const validationSchema = useMemo(
    () => createValidationSchema(nameValidation, mode),
    [nameValidation, mode]
  );

  return (
    <Formik<ObservabilityK8sFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={mode === 'edit'}
      validateOnMount
    >
      <Form className="form-horizontal">
        <Widget>
          <WidgetBody>
            {mode === 'create' && (
              <PolicyConfigurationFormSection policyType="observability-k8s" />
            )}
            <ObservabilityFormFields mode={mode} policyId={policy?.Id} />
            {mode === 'edit' && (
              <PolicyFormFooter
                mode={mode}
                policyType="observability-k8s"
                policyId={policy?.Id ?? null}
                originalEnvironmentGroupIds={originalEnvironmentGroupIds}
              />
            )}
          </WidgetBody>
        </Widget>
        {mode === 'create' && (
          <PolicyFormFooter
            mode={mode}
            policyType="observability-k8s"
            policyId={policy?.Id ?? null}
            onBack={onBack}
            canGoBack={canGoBack}
          />
        )}
      </Form>
    </Formik>
  );
}

function ObservabilityFormFields({
  mode,
  policyId,
}: {
  mode: 'create' | 'edit';
  policyId?: number;
}) {
  const { values, setFieldValue, setFieldTouched, errors, touched } =
    useFormikContext<ObservabilityK8sFormValues>();

  const isDeployMode = values.mode === 'deploy-and-connect';
  const isHttps = values.oneUptimeURL.startsWith('https://');

  return (
    <>
      <FormControl label="Description" errors={errors.description} size="large">
        <Input
          type="text"
          value={values.description}
          onChange={(e) => setFieldValue('description', e.target.value)}
          placeholder="Optional description"
          data-cy="observability-description"
        />
      </FormControl>

      <FormSectionTitle>OneUptime Connection</FormSectionTitle>

      <FormControl
        label="OneUptime URL"
        required
        errors={touched.oneUptimeURL ? errors.oneUptimeURL : undefined}
        size="large"
      >
        <Input
          type="text"
          value={values.oneUptimeURL}
          onChange={(e) => {
            const newURL = e.target.value;
            setFieldValue('oneUptimeURL', newURL);
            setFieldTouched('oneUptimeURL', true, false);
            if (!newURL.startsWith('https://')) {
              setFieldValue('tlsSkipVerify', false);
            }
          }}
          placeholder="https://oneuptime.com"
          data-cy="observability-oneuptime-url"
        />
      </FormControl>

      <FormControl
        label="Skip TLS verification"
        tooltip="Disable certificate verification for self-signed certificates. Only applicable for HTTPS URLs."
        size="large"
      >
        <Switch
          id="tls-skip-verify"
          name="tlsSkipVerify"
          checked={values.tlsSkipVerify && isHttps}
          disabled={!isHttps}
          onChange={(value) => setFieldValue('tlsSkipVerify', value)}
          data-cy="observability-tls-skip-verify"
        />
      </FormControl>

      <FormControl
        label="Project ID"
        required
        errors={touched.projectID ? errors.projectID : undefined}
        size="large"
      >
        <Input
          type="text"
          value={values.projectID}
          onChange={(e) => {
            setFieldValue('projectID', e.target.value);
            setFieldTouched('projectID', true, false);
          }}
          placeholder="Project ID"
          data-cy="observability-project-id"
        />
      </FormControl>

      <FormControl
        label="API key"
        required={mode === 'create'}
        errors={touched.apiKey ? errors.apiKey : undefined}
        size="large"
      >
        <Input
          type="password"
          autoComplete="new-password"
          value={values.apiKey}
          onChange={(e) => {
            setFieldValue('apiKey', e.target.value);
            setFieldTouched('apiKey', true, false);
          }}
          placeholder={
            mode === 'create'
              ? 'Enter API key'
              : 'Leave empty to preserve stored value'
          }
          data-cy="observability-api-key"
        />
      </FormControl>

      <FormControl
        label="Allow automatic key rotation"
        tooltip="When enabled, Portainer will automatically rotate the OneUptime API key before it expires. The current key must have rotation permissions — verify this using the test button below before saving."
        size="large"
      >
        <Switch
          id="auto-rotate-key"
          name="autoRotateKey"
          checked={values.autoRotateKey}
          onChange={(value) => setFieldValue('autoRotateKey', value)}
          data-cy="observability-auto-rotate-key"
        />
      </FormControl>

      <ConnectionVerifiedReset />

      <div className="form-group">
        <div className="col-sm-12">
          <ObservabilityTestConnection
            url={values.oneUptimeURL}
            apiKey={values.apiKey}
            projectID={values.projectID}
            tlsSkipVerify={values.tlsSkipVerify && isHttps}
            policyId={policyId}
            autoRotateKey={values.autoRotateKey}
            autoProvisionIngestionKey={
              isDeployMode && values.deploy.autoProvisionIngestionKey
            }
            onTestSuccess={() => setFieldValue('connectionVerified', true)}
          />
          {values.autoRotateKey && !values.connectionVerified && (
            <TextTip color="orange" className="mt-2">
              Test connection and permission must pass before saving with
              automatic key rotation enabled.
            </TextTip>
          )}
          {isDeployMode &&
            values.deploy.autoProvisionIngestionKey &&
            !values.connectionVerified && (
              <TextTip color="orange" className="mt-2">
                Test connection and permission must pass before saving with
                auto-provision ingestion key enabled.
              </TextTip>
            )}
        </div>
      </div>

      <FormSectionTitle>Mode</FormSectionTitle>

      <div className="form-group">
        <div className="col-sm-12">
          <div className="flex gap-4">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="mode"
                value="connect-only"
                checked={values.mode === 'connect-only'}
                onChange={() => setFieldValue('mode', 'connect-only')}
                data-cy="observability-mode-connect-only"
              />
              Connect to existing agents
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="mode"
                value="deploy-and-connect"
                checked={values.mode === 'deploy-and-connect'}
                onChange={() => setFieldValue('mode', 'deploy-and-connect')}
                data-cy="observability-mode-deploy-and-connect"
              />
              Deploy agents and connect
            </label>
          </div>
        </div>
      </div>

      {isDeployMode && (
        <>
          <FormSectionTitle>Agent Deployment</FormSectionTitle>

          <FormControl
            label="Auto-provision ingestion key"
            tooltip="When enabled, Portainer will obtain the ingestion key from OneUptime automatically on each save. The API key must have the CreateTelemetryIngestionKey permission — verify this using the test button above."
            size="large"
          >
            <Switch
              id="auto-provision-ingestion-key"
              name="deploy.autoProvisionIngestionKey"
              checked={values.deploy.autoProvisionIngestionKey}
              onChange={(value) => {
                setFieldValue('deploy.autoProvisionIngestionKey', value);
                if (value) {
                  setFieldValue('deploy.ingestionKey', '');
                }
              }}
              data-cy="observability-auto-provision-ingestion-key"
            />
          </FormControl>

          {!values.deploy.autoProvisionIngestionKey && (
            <FormControl
              label="Ingestion Key"
              required
              tooltip="Ingestion Key from OneUptime (Settings > Ingestion Keys). Used by the agent to send telemetry data."
              errors={
                touched.deploy?.ingestionKey
                  ? errors.deploy?.ingestionKey
                  : undefined
              }
              size="large"
            >
              <Input
                type="password"
                value={values.deploy.ingestionKey}
                onChange={(e) => {
                  setFieldValue('deploy.ingestionKey', e.target.value);
                  setFieldTouched('deploy.ingestionKey', true, false);
                }}
                placeholder="Enter ingestion key"
                data-cy="observability-ingestion-key"
              />
            </FormControl>
          )}

          <FormControl
            label="Agent-facing OneUptime URL"
            tooltip="URL the deployed agent uses to reach OneUptime from inside the cluster. Leave empty to use the OneUptime URL above. Set this when the cluster reaches OneUptime on a different address than the Portainer server — for example local clusters (http://host.docker.internal) or an internal load balancer."
            errors={errors.deploy?.agentURL}
            size="large"
          >
            <Input
              type="text"
              value={values.deploy.agentURL}
              onChange={(e) => {
                setFieldValue('deploy.agentURL', e.target.value);
                setFieldTouched('deploy.agentURL', true, false);
              }}
              placeholder="Defaults to the OneUptime URL above"
              data-cy="observability-agent-url"
            />
          </FormControl>

          <FormControl
            label="Target namespace"
            required
            errors={
              touched.deploy?.targetNamespace
                ? errors.deploy?.targetNamespace
                : undefined
            }
            size="large"
          >
            <Input
              type="text"
              value={values.deploy.targetNamespace}
              onChange={(e) => {
                setFieldValue('deploy.targetNamespace', e.target.value);
                setFieldTouched('deploy.targetNamespace', true, false);
              }}
              placeholder="oneuptime"
              data-cy="observability-target-namespace"
            />
          </FormControl>

          <FormControl
            label="Helm release name"
            required
            errors={
              touched.deploy?.helmReleaseName
                ? errors.deploy?.helmReleaseName
                : undefined
            }
            size="large"
          >
            <Input
              type="text"
              value={values.deploy.helmReleaseName}
              onChange={(e) => {
                setFieldValue('deploy.helmReleaseName', e.target.value);
                setFieldTouched('deploy.helmReleaseName', true, false);
              }}
              placeholder="oneuptime"
              data-cy="observability-helm-release-name"
            />
          </FormControl>

          <FormSectionTitle>Cluster Distribution</FormSectionTitle>

          <div className="form-group">
            <div className="col-sm-12">
              <div className="flex flex-col gap-2">
                {OBSERVABILITY_PRESETS.map(({ value, label, description }) => (
                  <label
                    key={value}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <input
                      type="radio"
                      name="deploy.preset"
                      value={value}
                      checked={values.deploy.preset === value}
                      onChange={() => setFieldValue('deploy.preset', value)}
                      data-cy={`observability-preset-${value}`}
                    />
                    <span>
                      <strong>{label}</strong> — {description}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <FormSectionTitle>Log Collection</FormSectionTitle>

          <FormControl
            label="Log collection mode"
            tooltip="Leave on 'Preset default' to let the preset decide (standard → DaemonSet, gke-autopilot / eks-fargate → API). Override explicitly if you know your cluster better than the preset does."
            size="large"
          >
            <PortainerSelect<ObservabilityLogsMode>
              value={values.deploy.logsMode}
              options={LOGS_MODE_OPTIONS}
              onChange={(value) =>
                setFieldValue('deploy.logsMode', value ?? '')
              }
              data-cy="observability-logs-mode"
            />
          </FormControl>
        </>
      )}

      {!isDeployMode && (
        <>
          <FormSectionTitle>Existing Agent Validation</FormSectionTitle>

          <FormControl
            label="Expected namespace"
            errors={errors.detection?.expectedNamespace}
            size="large"
          >
            <Input
              type="text"
              value={values.detection.expectedNamespace}
              onChange={(e) =>
                setFieldValue('detection.expectedNamespace', e.target.value)
              }
              placeholder="oneuptime-agent"
              data-cy="observability-expected-namespace"
            />
          </FormControl>

          <FormControl
            label="Expected Helm release name"
            errors={errors.detection?.helmReleaseName}
            size="large"
          >
            <Input
              type="text"
              value={values.detection.helmReleaseName}
              onChange={(e) =>
                setFieldValue('detection.helmReleaseName', e.target.value)
              }
              placeholder="kubernetes-agent"
              data-cy="observability-detection-helm-release-name"
            />
          </FormControl>
        </>
      )}
    </>
  );
}

function createValidationSchema(
  nameValidation: ReturnType<typeof useNameValidation>,
  mode: 'create' | 'edit'
): SchemaOf<ObservabilityK8sFormValues> {
  const deploySchema = object({
    ingestionKey: string().default(''),
    autoProvisionIngestionKey: boolean().required().default(false),
    agentURL: string().default(''),
    targetNamespace: string().default(''),
    helmReleaseName: string().default(''),
    preset: string()
      .oneOf(['standard', 'gke-autopilot', 'eks-fargate'] as const)
      .default('standard') as SchemaOf<ObservabilityPreset>,
    logsMode: string()
      .oneOf(['', 'api', 'daemonset', 'disabled'] as const)
      .default('') as SchemaOf<ObservabilityLogsMode>,
  });

  const detectionSchema = object({
    expectedNamespace: string().default(''),
    helmReleaseName: string().default(''),
  });

  return object({
    name: nameValidation,
    description: string().default(''),
    environmentGroupIds: array(number().required()),
    oneUptimeURL: string()
      .required('OneUptime URL is required')
      .url('Must be a valid URL'),
    apiKey:
      mode === 'create'
        ? string().required('API Key is required').default('')
        : string().default(''),
    projectID: string().required('Project ID is required'),
    mode: string()
      .oneOf(['connect-only', 'deploy-and-connect'] as const)
      .required() as SchemaOf<ObservabilityK8sFormValues['mode']>,
    deploy: deploySchema.when('mode', {
      is: 'deploy-and-connect',
      then: (s) =>
        s
          .shape({
            ingestionKey: string()
              .default('')
              .when('autoProvisionIngestionKey', {
                is: false,
                then: (ks) =>
                  mode === 'create'
                    ? ks.required('Ingestion key is required')
                    : ks,
              }),
            autoProvisionIngestionKey: boolean().required().default(false),
            agentURL: string().default('').url('Must be a valid URL'),
            targetNamespace: string()
              .default('')
              .required('Target namespace is required'),
            helmReleaseName: string()
              .default('')
              .required('Helm release name is required'),
            preset: string()
              .oneOf(['standard', 'gke-autopilot', 'eks-fargate'] as const)
              .default('standard') as SchemaOf<ObservabilityPreset>,
            logsMode: string()
              .oneOf(['', 'api', 'daemonset', 'disabled'] as const)
              .default('') as SchemaOf<ObservabilityLogsMode>,
          })
          .test(
            'agent-url-required-for-loopback',
            'Agent-facing OneUptime URL is required',
            function validateAgentURLForLoopback(deploy) {
              const root = this.parent as ObservabilityK8sFormValues;
              if (!deploy?.agentURL && isLoopbackURL(root?.oneUptimeURL)) {
                return this.createError({
                  path: 'deploy.agentURL',
                  message:
                    'The OneUptime URL is a loopback address that agents inside the cluster cannot reach — set an agent-facing URL (e.g. http://host.docker.internal).',
                });
              }
              return true;
            }
          ),
    }) as SchemaOf<ObservabilityK8sFormValues['deploy']>,
    detection: detectionSchema,
    tlsSkipVerify: boolean().required().default(false),
    autoRotateKey: boolean().required().default(false),
    connectionVerified: boolean()
      .required()
      .default(false)
      .when('autoRotateKey', {
        is: true,
        then: (s) =>
          s.isTrue(
            'Test connection and permission must pass before saving with automatic key rotation enabled.'
          ),
      })
      .when(['mode', 'deploy'], {
        is: (
          modeVal: string,
          deployVal: { autoProvisionIngestionKey?: boolean }
        ) =>
          modeVal === 'deploy-and-connect' &&
          deployVal?.autoProvisionIngestionKey === true,
        then: (s) =>
          s.isTrue(
            'Test connection and permission must pass before saving with auto-provision ingestion key enabled.'
          ),
      }),
  }) as SchemaOf<ObservabilityK8sFormValues>;
}
